import {Auth} from '@/api/models/Auth.ts';

interface Jwt {
	exp: number,
	iat: number,
	roles: Role[],
	username: string,
}

export enum Role {
	User = 'ROLE_USER',
}

export class Token {
	public readonly contents: Jwt;

	public constructor(
		public readonly jwt: string,
	) {
		const body = jwt.substring(jwt.indexOf('.') + 1, jwt.lastIndexOf('.'));
		this.contents = JSON.parse(atob(body));
	}

	public static tryCreate(jwt: string): Token | null {
		try {
			return new Token(jwt);
		} catch (e) {
			console.error(e);
			return null;
		}
	}

	public isExpired(): boolean {
		return this.contents.exp <= Math.floor(Date.now() / 1000);
	}
}

export class TokenStorage {
	protected token: Token | null = null;
	protected refreshTask: RefreshTask | null = null;

	public constructor(
		public readonly storageKey: string = 'inventory_hub_token',
	) {
		const jwt = window.localStorage.getItem(this.storageKey);

		if (jwt !== null)
			this.update(jwt);
	}

	public getToken(): Token | null {
		return this.token;
	}

	public update(jwt: string | Token | null): void {
		if (jwt === null)
			this.token = null;
		else if (jwt instanceof Token)
			this.token = jwt;
		else
			this.token = Token.tryCreate(jwt);

		this.refreshTask?.stop();

		if (!this.token || this.token.isExpired()) {
			this.token = null;
			return;
		}

		this.refreshTask = new RefreshTask(this.token, token => this.update(token));
		window.localStorage.setItem(this.storageKey, this.token.jwt);
	}

	public clear(): void {
		this.update(null);
	}
}

type TokenUpdatedFn = (token: Token | null) => void;

class RefreshTask {
	protected readonly jwt: string;
	protected readonly delay: number;
	protected taskId: number;

	public constructor(token: Token, protected callback: TokenUpdatedFn) {
		this.jwt = token.jwt;
		this.delay = Math.max(token.contents.exp * 1_000 - Date.now() - 5_000, 5_000);

		this.taskId = this.start();
	}

	public stop(): void {
		if (this.taskId >= 0) {
			this.taskId = -1;
			window.clearTimeout(this.taskId);
		}
	}

	protected start(): number {
		if (this.delay <= 5_000) {
			this.callback(null);
			return -1;
		}

		return window.setTimeout(() => this.refresh(), this.delay);
	}

	protected async refresh(): Promise<void> {
		this.taskId = -1;

		try {
			this.callback(await Auth.refresh());
		} catch (e) {
			console.error(e);
			this.callback(null);
		}
	}
}
